import { useMemo, useState, type ReactNode } from 'react';
import { Check, Plus } from 'lucide-react';
import { SearchField } from './Controls';
import { Group } from './List';
import { Sheet, SheetBack } from './Sheet';

export interface PickerItem {
  id: string;
  name: string;
}

/**
 * The picker's body: search, "Add new" (or `Add "<query>"`) first, then
 * Recent and the full list. Rendered inside the sheet that asked for it, as a
 * pushed panel (`PickerSheetPanel`), or on its own (`Picker`).
 */
export function PickerList<T extends PickerItem>({
  items,
  recent = [],
  value,
  onSelect,
  onCreate,
  noun,
  nounPlural = `${noun}s`,
  leading
}: {
  items: T[];
  recent?: T[];
  value: string | null;
  onSelect: (item: T) => void;
  onCreate?: (name: string) => Promise<T | undefined>;
  noun: string;
  nounPlural?: string;
  leading?: (item: T) => ReactNode;
}) {
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const query = q.trim();
  const shown = useMemo(() => {
    const s = query.toLowerCase();
    return s ? items.filter((i) => i.name.toLowerCase().includes(s)) : items;
  }, [items, query]);
  const exact = items.some((i) => i.name.toLowerCase() === query.toLowerCase());

  const row = (item: T) => (
    <button
      key={item.id}
      type="button"
      onClick={() => onSelect(item)}
      className="flex min-h-[52px] w-full items-center gap-3 px-4 text-left active:bg-[var(--pressed)]"
      style={leading ? { ['--inset' as string]: '38px' } : undefined}
    >
      {leading?.(item)}
      <span className="flex-1 truncate">{item.name}</span>
      {item.id === value && <Check className="size-5 text-[var(--accent)]" strokeWidth={2.4} />}
    </button>
  );

  return (
    <div className="flex flex-col gap-4">
      <SearchField value={q} onChange={setQ} placeholder={`Search ${nounPlural}`} />
      {onCreate && (
        <Group>
          <button
            type="button"
            disabled={creating}
            onClick={async () => {
              if (!query) {
                // Nothing typed: focus the search field, which is where a name goes.
                document.querySelector<HTMLInputElement>('input[type="search"]')?.focus();
                return;
              }
              setCreating(true);
              try {
                const created = await onCreate(query);
                if (created) onSelect(created);
              } finally {
                setCreating(false);
              }
            }}
            className="flex min-h-[52px] w-full items-center gap-3 px-4 text-left font-medium text-[var(--accent)] active:bg-[var(--pressed)] disabled:opacity-50"
          >
            <Plus className="size-5" strokeWidth={2.2} />
            <span className="truncate">{query && !exact ? `Add “${query}”` : `Add new ${noun}`}</span>
          </button>
        </Group>
      )}
      {!query && recent.length > 0 && <Group header="Recent">{recent.map(row)}</Group>}
      {shown.length > 0 ? (
        <Group header={query ? undefined : `All ${nounPlural}`}>{shown.map(row)}</Group>
      ) : (
        !onCreate && <p className="py-6 text-center text-secondary text-[var(--text-muted)]">No {nounPlural} match.</p>
      )}
    </div>
  );
}

/** A picker on its own sheet, for screens that are not already a sheet. */
export function Picker<T extends PickerItem>({
  open,
  onClose,
  title,
  backLabel = 'Back',
  ...list
}: Parameters<typeof PickerList<T>>[0] & { open: boolean; onClose: () => void; title: string; backLabel?: string }) {
  return (
    <Sheet open={open} onClose={onClose} full title={title} leading={<SheetBack label={backLabel} onClick={onClose} />}>
      <PickerList
        {...list}
        onSelect={(item) => {
          list.onSelect(item);
          onClose();
        }}
      />
    </Sheet>
  );
}
